import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Download, Lightbulb, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable, type DataTableColumn } from '@/shared/components/data-table';
import { PageShell, UnderlineTabs, type UnderlineTabsTab } from '@/shared/components';
import { usePersistedTab } from '@/shared/hooks/usePersistedFilters';
import { useClientSideTable } from '@/shared/hooks/useClientSideTable';
import { useHasAnyRole } from '@/hooks/useSession';
import { copyCSVToClipboard, downloadCSV, toCSV, type CSVColumn } from '@/lib/csv';
import { formatDate } from '@/lib/utils';
import { CATEGORY_LABEL, CUSTOMER_REQUEST_STATUS_ROLES, STATUS_LABEL, STATUSES } from '../constants';
import { useCustomerRequests } from '../hooks/useCustomerRequests';
import { CustomerRequestPanel } from '../components/CustomerRequestPanel';
import { CustomerRequestStatusBadge } from '../components/CustomerRequestStatusBadge';
import { CustomerName } from '../components/CustomerName';
import { formatArr, formatSensors } from '../format';
import type { CustomerRequestStatus, CustomerRequestSummary } from '../types';

type Tab = CustomerRequestStatus | 'all';
const VALID_TABS: readonly Tab[] = [...STATUSES.map((s) => s.value), 'all'];

function customerNames(r: CustomerRequestSummary) {
  return r.customers.map((c) => c.name).join(', ');
}

const COLUMNS: DataTableColumn<CustomerRequestSummary>[] = [
  {
    key: 'votes', label: 'Votes', sortKey: 'votes', noFilter: true, className: 'w-20',
    getValue: (r) => String(r.votes),
    render: (r) => (
      <span className="inline-block min-w-9 rounded-md border px-1.5 py-0.5 text-center font-bold tabular-nums">▲ {r.votes}</span>
    ),
  },
  {
    key: 'title', label: 'Request', sortKey: 'title', isPrimarySearch: true, searchPlaceholder: 'Search requests...',
    getValue: (r) => r.title,
    render: (r) => (
      <div className="min-w-0">
        <p className="font-medium">{r.title}</p>
        <p className="text-xs text-muted-foreground">
          {[r.category && CATEGORY_LABEL[r.category], r.createdBy.name && `added by ${r.createdBy.name}`].filter(Boolean).join(' · ')}
        </p>
      </div>
    ),
  },
  {
    key: 'customers', label: 'Customers', filterType: 'text',
    getValue: customerNames,
    render: (r) => r.customers.length
      ? (
        <span>
          {r.customers.slice(0, 2).map((c, i) => (
            <span key={c.id}>{i > 0 && ', '}<CustomerName name={c.name} status={c.status} /></span>
          ))}
          {r.customers.length > 2 && ` +${r.customers.length - 2}`}
        </span>
      )
      : <i className="text-muted-foreground">no customer named</i>,
  },
  {
    key: 'revenue', label: 'ARR', sortKey: 'revenue', noFilter: true, className: 'text-right',
    getValue: (r) => String(r.revenue),
    render: (r) => formatArr(r.revenue),
  },
  {
    key: 'activeSensors', label: 'Active sensors', sortKey: 'activeSensors', noFilter: true, className: 'text-right',
    getValue: (r) => String(r.activeSensors),
    render: (r) => formatSensors(r.activeSensors),
  },
  {
    key: 'status', label: 'Status', sortKey: 'status', noFilter: true,
    getValue: (r) => STATUS_LABEL[r.status],
    render: (r) => <CustomerRequestStatusBadge status={r.status} />,
  },
  {
    key: 'lastAskedAt', label: 'Last asked', sortKey: 'lastAskedAt', noFilter: true,
    getValue: (r) => r.lastAskedAt ?? '',
    render: (r) => formatDate(r.lastAskedAt),
  },
];

const CSV_COLUMNS: CSVColumn<CustomerRequestSummary>[] = [
  { header: 'Votes', value: (r) => r.votes },
  { header: 'Request', value: (r) => r.title },
  { header: 'Category', value: (r) => (r.category ? CATEGORY_LABEL[r.category] : '') },
  { header: 'Status', value: (r) => STATUS_LABEL[r.status] },
  { header: 'Customers', value: customerNames },
  { header: 'ARR', value: (r) => r.revenue },
  { header: 'Active sensors', value: (r) => r.activeSensors },
  { header: 'Last asked', value: (r) => formatDate(r.lastAskedAt) },
  { header: 'Description', value: (r) => r.description ?? '' },
];

// Sort numbers as numbers; the column getValue strings are for filtering.
function columnValue(r: CustomerRequestSummary, key: string): unknown {
  if (key === 'title') return `${r.title} ${r.description ?? ''}`; // the search box matches both
  if (key === 'customers') return customerNames(r);
  if (key === 'status') return STATUS_LABEL[r.status];
  return (r as unknown as Record<string, unknown>)[key];
}

export function CustomerRequestsPage() {
  const [tab, setTab] = usePersistedTab<Tab>('customer_requests', VALID_TABS, 'open');
  const [params, setParams] = useSearchParams();
  const selected = params.get('id');
  const containerRef = useRef<HTMLDivElement>(null);
  const canChangeStatus = useHasAnyRole(CUSTOMER_REQUEST_STATUS_ROLES);
  const { data, isLoading } = useCustomerRequests(tab);

  const select = (next: 'new' | string | null) => {
    const p = new URLSearchParams(params);
    if (next) p.set('id', next); else p.delete('id');
    setParams(p, { replace: true });
  };

  // The API returns votes desc, then most recently asked; a stable sort on votes keeps that tie order.
  const table = useClientSideTable({
    items: data?.requests ?? [],
    getColumnValue: columnValue,
    initialSortKey: 'votes',
    initialSortDirection: -1,
  });

  const tabs: UnderlineTabsTab<Tab>[] = useMemo(() => [
    ...STATUSES.map((s) => ({ value: s.value, label: s.label, badge: data?.counts[s.value] })),
    { value: 'all' as const, label: 'All', badge: data?.counts.all },
  ], [data?.counts]);

  const csv = () => toCSV(table.sortedItems, CSV_COLUMNS);

  return (
    <PageShell
      Icon={Lightbulb}
      subtitle="What customers ask us to build, ranked by how many customers asked"
      actions={(
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => copyCSVToClipboard(csv()).then(() => toast.success('Copied'))}>
            <Copy className="h-4 w-4 mr-2" />Copy
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadCSV('customer-requests.csv', csv())}>
            <Download className="h-4 w-4 mr-2" />CSV
          </Button>
          <Button onClick={() => select('new')}>
            <Plus className="h-4 w-4 mr-2" />New request
          </Button>
        </div>
      )}
      tabs={<UnderlineTabs tabs={tabs} active={tab} onChange={setTab} />}
    >
      <div ref={containerRef} className="flex min-h-[calc(100vh-240px)] gap-2">
        <div className="flex-1 min-w-0 overflow-auto">
          <DataTable<CustomerRequestSummary, undefined>
            items={table.sortedItems}
            columns={COLUMNS as DataTableColumn<CustomerRequestSummary, undefined>[]}
            ctx={undefined}
            columnFilters={table.columnFilters}
            sortKey={table.sortKey}
            sortDirection={table.sortDirection}
            isLoading={isLoading}
            onSort={table.setSort}
            onSetColumnFilter={table.setColumnFilter}
            onRowClick={(r) => select(r.id)}
            emptyState={(
              <>
                {table.isFiltered ? 'No request matches these filters.' : 'No requests here yet.'}{' '}
                <Button variant="link" size="sm" onClick={() => select('new')}>
                  <Plus className="h-4 w-4 mr-1" />Add one
                </Button>
              </>
            )}
          />
        </div>
        <CustomerRequestPanel
          selected={selected}
          onSelect={select}
          canChangeStatus={canChangeStatus}
          containerRef={containerRef}
        />
      </div>
    </PageShell>
  );
}
